import { useLazyQuery } from "@apollo/client";
import { Modal } from "antd";
import cuid from "cuid";
import { defaults, get, keyBy } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_QUALIFICATION } from "utils/api/graphql/mutations/qualifications";
import { QUALIFIER } from "utils/api/graphql/queries/qualifiers";
import { CONTACT_POINT_SYSTEM, QUALIFIER_TAGS } from "utils/constants";
import EmailSent from "./EmailSent";
import View from "./View";

const HelpModal = ({ open, setOpen, project, onSubmit }) => {
  const [saved, setSaved] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.HELP_MODAL", { returnObjects: true });

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
    [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
  });

  const [getQualifier] = useLazyQuery(QUALIFIER, {
    variables: {
      where: {
        tag: QUALIFIER_TAGS.CALL_LATER,
      },
    },
  });

  const [addQualification] = useMutation(CREATE_QUALIFICATION);

  const initialValues = {
    contact: {
      ...get(project, "contact", {}),
      telecoms,
      acceptDigital: get(project, "contact.acceptDigital", false),
      acceptContact: get(project, "contact.acceptContact", false),
      fields: {
        ...get(project, "contact.fields", {}),
      },
    },
  };

  const onSubmitForm = (formData) => {
    const { contact } = formData;

    const telecomList = Object.entries(contact.telecoms || {})
      .filter(([, val]) => val?.value)
      .map(([system, val]) => ({
        id: val.id || cuid(),
        system,
        value: val.value,
      }));

    const contactId = get(project, "contact.id") || cuid();
    const userId = get(project, "contact.user.id") || cuid();

    const updatedContact = {
      id: contactId,
      acceptDigital: true,
      acceptContact: true,
      fields: contact?.fields ?? {},
      user: {
        id: userId,
        firstname: contact?.user?.firstname,
        lastname: contact?.user?.lastname,
      },
      telecoms: telecomList,
    };
    onSubmit({
      payload: {
        contact: updatedContact,
        visible: true,
      },
      onCompleted: () => {
        getQualifier({
          onCompleted: (data) => {
            if (!get(data, "qualifier.id")) return;
            addQualification({
              variables: {
                data: {
                  id: cuid(),
                  qualifier: { id: get(data, "qualifier.id") },
                  contact: { id: contactId },
                },
              },
            });
          },
        });
      },
    });

    setSaved(true);
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      width={saved ? 520 : 720}
      footer={null}
      className="LMF-help-modal"
    >
      {saved ? (
        <EmailSent setOpen={setOpen} TRANSLATION={TRANSLATION} />
      ) : (
        <View
          contact={{ ...get(project, "contact", {}), telecoms }}
          setOpen={setOpen}
          onSubmit={onSubmitForm}
          initialValues={initialValues}
          TRANSLATION={TRANSLATION}
          errorMessages={t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, {
            returnObjects: true,
          })}
        />
      )}
    </Modal>
  );
};

export default HelpModal;
